import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { GradeResult } from '@shared/grading'
import type { ExerciseResponse } from '@shared/schemas/exercise'
import type { LessonDetail } from '@shared/types/catalog'
import { api } from './api'
import { catalogKeys } from './catalog'

/**
 * Attempt submission.
 *
 * The client has already graded locally and shown feedback by the time this
 * runs, so submission is deliberately fire-and-forget from the UI's point of
 * view: she never waits on the network to see whether she was right. The
 * server's verdict is what persists, and a mismatch is logged rather than
 * shown, because contradicting feedback she has already read would be worse
 * than a silently corrected record.
 */

export interface SubmitAttemptBody {
  attemptId: string
  exerciseId: string
  response: ExerciseResponse
  durationMs?: number
  clientCreatedAt?: number
}

export interface SubmitAttemptResponse {
  attemptId: string
  result: GradeResult
  xpEarned: number
  duplicate: boolean
}

const QUEUE_KEY = 'pj.attemptQueue.v1'

interface QueuedAttempt extends SubmitAttemptBody {
  queuedAt: number
}

function readQueue(): QueuedAttempt[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as QueuedAttempt[]) : []
  } catch {
    return []
  }
}

function writeQueue(items: QueuedAttempt[]): void {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(items))
  } catch {
    // A full or disabled storage must never break a lesson.
  }
}

export function enqueueAttempt(body: SubmitAttemptBody): void {
  const queue = readQueue()
  if (queue.some((q) => q.attemptId === body.attemptId)) return
  queue.push({ ...body, queuedAt: Date.now() })
  writeQueue(queue)
}

export function dequeueAttempt(attemptId: string): void {
  writeQueue(readQueue().filter((q) => q.attemptId !== attemptId))
}

export async function submitAttempt(body: SubmitAttemptBody): Promise<SubmitAttemptResponse> {
  return api<SubmitAttemptResponse>('/attempts', { method: 'POST', body })
}

/**
 * Drains queued attempts. Safe to call repeatedly: every attempt carries a
 * client-generated id and the server ignores a repeat, so a double flush
 * cannot double-count progress.
 */
export async function flushAttemptQueue(): Promise<number> {
  const queue = readQueue()
  let flushed = 0
  for (const item of queue) {
    try {
      const { queuedAt: _queuedAt, ...body } = item
      await submitAttempt(body)
      dequeueAttempt(item.attemptId)
      flushed++
    } catch {
      // Still offline or the server is unhappy — leave the rest queued and
      // stop, rather than hammering a failing endpoint.
      break
    }
  }
  return flushed
}

export function useSubmitAttempt() {
  return useMutation({
    mutationFn: submitAttempt,
    onMutate: (body: SubmitAttemptBody) => {
      // Queue first, then send. If the request never lands, the attempt is
      // already durable; if it succeeds, it is removed below.
      enqueueAttempt(body)
    },
    onSuccess: (data, body) => {
      dequeueAttempt(body.attemptId)
      if (import.meta.env.DEV && data.result.correct !== undefined) {
        // Surfaces a disagreement between the local and server graders during
        // development, where it is a bug worth fixing rather than noise.
        console.debug('[attempt] server verdict', data.result.correct, 'xp', data.xpEarned)
      }
    },
  })
}

/**
 * Records how far through a lesson she has read.
 *
 * Fire-and-forget on purpose: this is a convenience, not data she would miss
 * if a single request failed, and blocking the Continue button on a network
 * round trip would make the whole lesson feel sluggish. The server only ever
 * moves the stored position forward, so out-of-order arrivals are harmless.
 */
export function useSaveLessonProgress(lessonId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { lastPosition: number }) =>
      api<void>(`/lessons/${lessonId}/progress`, { method: 'POST', body }),
    onMutate: ({ lastPosition }) => {
      // Lesson content is cached for a day so it works offline, which means a
      // second visit reads `lastPosition` from that cache rather than the
      // server. Without this write the cached copy would still say 0 and the
      // resume would silently do nothing.
      queryClient.setQueryData<LessonDetail>(catalogKeys.lesson(lessonId), (previous) =>
        previous ? { ...previous, lastPosition } : previous,
      )
    },
    // A failed save just means she resumes slightly earlier next time.
    onError: () => undefined,
  })
}

export function useCompleteLesson(lessonId: string, courseId: string | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: { timeSpentMs: number }) =>
      api<{
        scorePct: number
        answered: number
        total: number
        xpEarned: number
        currentStreak: number
        alreadyCompleted: boolean
      }>(`/lessons/${lessonId}/complete`, { method: 'POST', body }),
    onSuccess: async () => {
      // Finishing resets the lesson: reopening it is a review from the top,
      // not a resume. Patched rather than invalidated so it stays right even
      // if the refetch never happens (offline).
      queryClient.setQueryData<LessonDetail>(catalogKeys.lesson(lessonId), (previous) =>
        previous ? { ...previous, status: 'completed', lastPosition: 0 } : previous,
      )
      // The course map's unlock state and the dashboard both change on
      // completion, so both caches are invalidated rather than patched.
      await queryClient.invalidateQueries({ queryKey: catalogKeys.courses })
      if (courseId) {
        await queryClient.invalidateQueries({ queryKey: catalogKeys.course(courseId) })
      }
      await queryClient.invalidateQueries({ queryKey: ['progress'] })
    },
  })
}
